import type { DeploymentDatabase, HealthState } from '@central/api';
import { errorMessage as sharedErrorMessage, formatDuration } from '@central/ui';

export const healthLabel: Record<HealthState, string> = {
  up: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
  updating: 'Updating',
};

const messages: Record<string, string> = {
  last_owner:
    'Your organization needs at least one enabled Owner. Make someone else an Owner first.',
  last_factor:
    'Your organization requires a second factor, and this is your only one. Add another first.',
  logs_not_visible: 'Logs aren’t shown for this deployment.',
  logs_not_shared: 'Logs aren’t shared for your organization.',
  files_unavailable: 'File uploads aren’t available right now. Send the message without files.',
};

/** User-facing text for a failed call, keyed by the server's error code. */
export function errorMessage(e: unknown): string {
  if (e instanceof Error && e.name === 'UploadError') return e.message;
  return sharedErrorMessage(e, messages);
}

/** "2.1 MB", "512 KB", "300 B" (decimal units, as file sizes are shown to customers). */
export function fileSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** "in 6 h", "in 18 min", "in 3 d"; "at the next report" once due. */
export function formatIn(iso: string, now: Date = new Date()): string {
  const s = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  if (s < 60) return 'at the next report';
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h} h`;
  return `in ${Math.floor(h / 24)} d`;
}

const databaseNames: Record<string, string> = { mongo: 'MongoDB', questdb: 'QuestDB' };

/** A database's product name; a name Central does not know is shown as reported. */
export function databaseLabel(name: string): string {
  return databaseNames[name] ?? name;
}

/** "3 ms · 41 connections · up 1 d 1 h": only the figures the deployment reports. */
export function databaseDetail(d: DeploymentDatabase): string {
  const parts: string[] = [];
  if (d.pingMs !== undefined) parts.push(`${d.pingMs} ms`);
  if (d.connections !== undefined)
    parts.push(`${d.connections} connection${d.connections === 1 ? '' : 's'}`);
  if (d.uptimeSeconds !== undefined) parts.push(`up ${formatDuration(d.uptimeSeconds)}`);
  if (d.backlog !== undefined) parts.push(`${d.backlog.toLocaleString('en-US')} waiting`);
  return parts.join(' · ');
}
